import React, { useState, useEffect } from 'react';
import './HelperPriceManager.css';

const ServicePriceCard = ({ service, priceData, onPriceChange, isSaving, saveMessage }) => {
  const [localPrice, setLocalPrice] = useState(priceData?.price || '');
  const [localOfferPrice, setLocalOfferPrice] = useState(priceData?.offered_price || '');
  const [localOfferDate, setLocalOfferDate] = useState(priceData?.offer_valid_upto || '');
  const [localDescription, setLocalDescription] = useState(priceData?.description || '');

  // Update local state when priceData changes
  useEffect(() => {
    if (priceData) {
      setLocalPrice(priceData.price || '');
      setLocalOfferPrice(priceData.offered_price || '');
      setLocalOfferDate(priceData.offer_valid_upto?.split('T')[0] || '');
      setLocalDescription(priceData.description || '');
    }
  }, [priceData]);

  const handleInputChange = (field, value) => {
    switch (field) {
      case 'price':
        setLocalPrice(value);
        break;
      case 'offered_price':
        setLocalOfferPrice(value);
        break;
      case 'offer_valid_upto':
        setLocalOfferDate(value);
        break;
      case 'description':
        setLocalDescription(value);
        break;
    }
    
    // Convert to appropriate format for API
    let apiValue = value;
    if (field === 'price' || field === 'offered_price') {
      apiValue = parseFloat(value) || 0;
    } else if (field === 'offer_valid_upto' && value) {
      apiValue = new Date(value).toISOString();
    }
    
    onPriceChange(service.id, field, apiValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="service-price-card">
      {/* Service Header */}
      <div className="service-header">
        <div className="service-image-container">
          {service.image_url ? (
            <img
              src={service.image_url}
              alt={service.name}
              className="service-image"
              loading="lazy"
            />
          ) : (
            <div className="service-image-placeholder">
              {service.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="service-info">
          <h4 className="service-name">{service.name}</h4>
          <p className="service-description">{service.description || 'No description available'}</p>
          <div className="service-meta">
            <span className={`service-status ${service.is_active ? 'active' : 'inactive'}`}>
              {service.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Price Inputs */}
      <div className="price-inputs">
        <div className="input-group">
          <label className="input-label">
            Regular Price ($)
            {isSaving && <span className="saving-indicator">Saving...</span>}
          </label>
          <input
            type="number"
            className="price-input"
            value={localPrice}
            onChange={(e) => handleInputChange('price', e.target.value)}
            placeholder="Enter price"
            min="0"
            step="0.01"
          />
        </div>

        <div className="input-group">
          <label className="input-label">
            Offer Price ($)
            {localOfferPrice && (
              <span className="discount-badge">
                {priceData?.price ? 
                  `${Math.round((1 - localOfferPrice/priceData.price) * 100)}% OFF` : 
                  'OFFER'
                }
              </span>
            )}
          </label>
          <input
            type="number"
            className="price-input offer-price"
            value={localOfferPrice}
            onChange={(e) => handleInputChange('offered_price', e.target.value)}
            placeholder="Optional offer price"
            min="0"
            step="0.01"
          />
        </div>
      </div>

      {/* Offer Date */}
      <div className="offer-date-input">
        <label className="input-label">Offer Valid Until</label>
        <input
          type="date"
          className="date-input"
          value={localOfferDate}
          onChange={(e) => handleInputChange('offer_valid_upto', e.target.value)}
          min={new Date().toISOString().split('T')[0]}
        />
        {priceData?.offer_valid_upto && (
          <p className="offer-date-display">
            Valid until: {formatDate(priceData.offer_valid_upto)}
          </p>
        )}
      </div>

      {/* Description */}
      <div className="description-input">
        <label className="input-label">Description (Optional)</label>
        <textarea
          className="description-textarea"
          value={localDescription}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Add any special notes or terms..."
          rows="2"
        />
      </div>

      {/* Save Status Message */}
      {saveMessage && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.message}
        </div>
      )}

      {/* Price Status */}
      <div className="price-status">
        {priceData ? (
          <div className="status-indicator active">
            <span className="status-dot"></span>
            Price configured
          </div>
        ) : (
          <div className="status-indicator inactive">
            <span className="status-dot"></span>
            No price set
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicePriceCard;